import { Static, Type } from '@sinclair/typebox';
import { AiFieldKind } from './ai-assist';
import { CatalogSelectionInputSchema, CatalogSelectionResponseSchema } from './catalog-selection';
import { ReservationAmountsSchema } from './reservation_discount';
import { NotificationChannel } from './notification';
import {
  ProPaymentEventType,
  ProPaymentStatus,
  ProPaymentType,
  ProPaymentTypeSchema,
} from './translation_namespace/pro_payment_namespace';

export const MIN_PRO_PAYMENT_AMOUNT = 50;

/** Statuts « argent encaissé » : le paiement a été réglé, même s'il a ensuite été remboursé
 *  (le remboursement se déduit du net, pas du périmètre). Exclut WAITING_PAYMENT, AUTHORIZED
 *  (empreinte non capturée / CARD_SAVE), CANCELLED et EXPIRED. Source unique de l'onglet « Ventes »
 *  de la fiche client.
 *  NB : pas de `as const` — un `readonly [...]` n'est pas assignable au `ProPaymentStatus[]` Prisma. */
export const COLLECTED_PRO_PAYMENT_STATUSES: ProPaymentStatus[] = [
  ProPaymentStatus.PAYED,
  ProPaymentStatus.MANUALLY_PAID,
  ProPaymentStatus.PARTIALLY_REFUNDED,
  ProPaymentStatus.REFUNDED,
];

// Une déclaration manuelle n'a pas de page de paiement : rien à envoyer au client.
const SEND_LINK_HIDDEN_WHEN = { field: 'payment_type', value: ProPaymentType.MANUAL_PAYMENT };

const createProPaymentStandaloneProperties = {
  pro_customer_id: Type.Integer({
    minimum: 1,
    meta: { input_type: 'pro_customer_selector' },
    placeholder: 'INPUT_PLACEHOLDER_PRO_PAYMENT_CUSTOMER',
    title: 'INPUT_TITLE_PRO_PAYMENT_CUSTOMER',
  }),
  name: Type.Optional(
    Type.String({
      maxLength: 100,
      placeholder: 'INPUT_PLACEHOLDER_PRO_PAYMENT_NAME',
      title: 'INPUT_TITLE_PRO_PAYMENT_NAME',
    }),
  ),
  payment_type: ProPaymentTypeSchema({
    placeholder: 'INPUT_PLACEHOLDER_PRO_PAYMENT_TYPE',
    title: 'INPUT_TITLE_PRO_PAYMENT_TYPE',
    meta: { multiple: false, disable_search: true },
  }),
  prestation_input: Type.Optional(
    Type.Array(CatalogSelectionInputSchema, {
      placeholder: 'INPUT_PLACEHOLDER_PRO_PAYMENT_PRESTATIONS',
      title: 'INPUT_TITLE_PRO_PAYMENT_PRESTATIONS',
      meta: { input_type: 'pack_prestations_selector', allow_supplements: true },
    }),
  ),
  amount: Type.Integer({
    minimum: MIN_PRO_PAYMENT_AMOUNT,
    meta: { is_price: true, auto_sum_from: 'prestation_input' },
    placeholder: 'INPUT_PLACEHOLDER_PRO_PAYMENT_AMOUNT',
    title: 'INPUT_TITLE_PRO_PAYMENT_AMOUNT',
  }),
  description: Type.Optional(
    Type.String({
      maxLength: 500,
      meta: { multiline: true, ai: { kind: AiFieldKind.SHORT_DESCRIPTION } },
      placeholder: 'INPUT_PLACEHOLDER_PRO_PAYMENT_DESCRIPTION',
      title: 'INPUT_TITLE_PRO_PAYMENT_DESCRIPTION',
    }),
  ),
  // Envoi du lien au client juste après la création (notification PAYMENT_REQUEST). Absent = rien n'est envoyé
  // (agenda, outils MCP) : le `default` ne pré-coche que le formulaire, le backend n'applique pas les défauts.
  send_link_email: Type.Optional(
    Type.Boolean({
      default: true,
      title: 'INPUT_TITLE_PRO_PAYMENT_SEND_LINK_EMAIL',
      meta: { hidden_when: SEND_LINK_HIDDEN_WHEN },
    }),
  ),
  send_link_sms: Type.Optional(
    Type.Boolean({
      default: false,
      title: 'INPUT_TITLE_PRO_PAYMENT_SEND_LINK_SMS',
      meta: { hidden_when: SEND_LINK_HIDDEN_WHEN },
    }),
  ),
};

export const CreateStandaloneProPaymentRequestSchema = Type.Object(createProPaymentStandaloneProperties);
export type CreateStandaloneProPaymentRequestObject = Static<typeof CreateStandaloneProPaymentRequestSchema>;

// --- Request: Pro creates a payment request ---
export const CreateProPaymentRequestSchema = Type.Object({
  ...createProPaymentStandaloneProperties,
  reservation_id: Type.Optional(
    Type.Integer({
      minimum: 1,
      placeholder: 'INPUT_PLACEHOLDER_PRO_PAYMENT_RESERVATION_ID',
      title: 'INPUT_TITLE_PRO_PAYMENT_RESERVATION_ID',
    }),
  ),
});
export type CreateProPaymentRequestObject = Static<typeof CreateProPaymentRequestSchema>;

// --- Envoi du lien de paiement au client (notification PAYMENT_REQUEST) ---
export const ProPaymentLinkChannelSchema = Type.Union([
  Type.Literal(NotificationChannel.EMAIL),
  Type.Literal(NotificationChannel.SMS),
]);
export type ProPaymentLinkChannel = Static<typeof ProPaymentLinkChannelSchema>;

export const SendProPaymentLinkRequestSchema = Type.Object({ channel: ProPaymentLinkChannelSchema });
export type SendProPaymentLinkRequestObject = Static<typeof SendProPaymentLinkRequestSchema>;

// Canaux d'envoi activés par le pro dans ses messages automatiques (événement PAYMENT_REQUEST).
export const ProPaymentLinkChannelsResponseSchema = Type.Object({ channels: Type.Array(ProPaymentLinkChannelSchema) });
export type ProPaymentLinkChannelsResponseObject = Static<typeof ProPaymentLinkChannelsResponseSchema>;

// --- Query: List pro payments ---
export const ListProPaymentQuerySchema = Type.Object({
  limit: Type.Number({ minimum: 1, maximum: 100, default: 20 }),
  offset: Type.Number({ minimum: 0, default: 0 }),
  status: Type.Optional(Type.Enum(ProPaymentStatus)),
});
export type ListProPaymentQueryObject = Static<typeof ListProPaymentQuerySchema>;

// --- Request: Update a pending payment amount ---
export const UpdateProPaymentRequestSchema = Type.Object({
  amount: Type.Integer({
    minimum: MIN_PRO_PAYMENT_AMOUNT,
    meta: { is_price: true },
    placeholder: 'INPUT_PLACEHOLDER_PRO_PAYMENT_AMOUNT',
    title: 'INPUT_TITLE_PRO_PAYMENT_AMOUNT',
  }),
});
export type UpdateProPaymentRequestObject = Static<typeof UpdateProPaymentRequestSchema>;

// --- Request: Capture a bank imprint ---
export const CaptureProPaymentRequestSchema = Type.Object({
  amount: Type.Integer({ minimum: 1, meta: { is_price: true } }),
});
export type CaptureProPaymentRequestObject = Static<typeof CaptureProPaymentRequestSchema>;

// --- Request: Refund a payment ---
export const RefundProPaymentRequestSchema = Type.Object({
  amount: Type.Integer({ minimum: 1, meta: { is_price: true } }),
  // Si true, re-crédite (en entier) le bon utilisé pour la réservation liée à ce paiement.
  restore_voucher: Type.Optional(Type.Boolean()),
  // Si true, recale le « reste à encaisser » (`remaining_amount_on_site`) sur `base_amount` − encaissé
  // net APRÈS ce remboursement. Réservé au remboursement d'un véhicule retiré d'une course : proposé au
  // retrait (le retrait a calculé le reste sur l'encaissé d'AVANT remboursement) ou case « véhicule
  // retiré » cochée dans la fiche paiement (cf. buildProPaymentRefundRequest). Absent / false : le reste
  // n'est jamais touché (geste commercial, remboursement fiche paiement sans la case…).
  sync_remaining_with_base: Type.Optional(
    Type.Boolean({
      description:
        "Retrait d'un véhicule d'une course uniquement : recale le reste à encaisser sur le total (base_amount) moins l'encaissé net après remboursement.",
    }),
  ),
});
export type RefundProPaymentRequestObject = Static<typeof RefundProPaymentRequestSchema>;

// --- Request: le pro déclare un paiement reçu hors plateforme (espèces, chèque…) sur une réservation ---
// Montant saisi par le pro (prérempli avec le reste à encaisser) : une réservation peut en recevoir
// plusieurs (acompte puis solde, partage…). Pas de minimum Stripe — rien ne transite par Stripe.
export const DeclareReservationManualPaidRequestSchema = Type.Object({
  amount: Type.Integer({
    minimum: 1,
    meta: { is_price: true },
    placeholder: 'INPUT_PLACEHOLDER_PRO_PAYMENT_AMOUNT',
    title: 'INPUT_TITLE_PRO_PAYMENT_AMOUNT',
  }),
});
export type DeclareReservationManualPaidRequestObject = Static<typeof DeclareReservationManualPaidRequestSchema>;

/**
 * Corps du remboursement lancé depuis le dialogue de la fiche paiement. `sync_remaining_with_base`
 * n'est envoyé que si le pro a coché « véhicule retiré » ET que le paiement le permet (course encore
 * active, cf. `can_sync_remaining_with_base`) ; sinon le corps reste exactement celui d'avant.
 */
export function buildProPaymentRefundRequest(params: {
  amount: number;
  restore_voucher: boolean;
  is_removed_vehicle_refund: boolean;
  can_sync_remaining_with_base: boolean;
}): RefundProPaymentRequestObject {
  const shouldSyncRemaining = params.is_removed_vehicle_refund && params.can_sync_remaining_with_base;
  return {
    amount: params.amount,
    restore_voucher: params.restore_voucher,
    ...(shouldSyncRemaining ? { sync_remaining_with_base: true } : {}),
  };
}

export const BookingCancellationRefundInfoSchema = Type.Object({
  cancellation_deadline: Type.Optional(Type.String({ format: 'date-time' })),
  cancellation_deadline_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'cancellation_deadline', with_time: true } }),
  ),
  refund_amount_in_time: Type.Optional(
    Type.String({ meta: { is_price: true, source_key: 'refund_amount_in_time_raw' } }),
  ),
  refund_amount_in_time_raw: Type.Optional(Type.Number()),
  refund_amount_out_time: Type.Optional(
    Type.String({ meta: { is_price: true, source_key: 'refund_amount_out_time_raw' } }),
  ),
  refund_amount_out_time_raw: Type.Optional(Type.Number()),
  current_refund_amount: Type.Optional(
    Type.String({ meta: { is_price: true, source_key: 'current_refund_amount_raw' } }),
  ),
  current_refund_amount_raw: Type.Optional(Type.Number()),
  late_cancel_penalty_amount: Type.Optional(
    Type.String({ meta: { is_price: true, source_key: 'late_cancel_penalty_amount_raw' } }),
  ),
  late_cancel_penalty_amount_raw: Type.Optional(Type.Number()),
  is_before_cancellation_deadline: Type.Optional(Type.Boolean()),
});
export type BookingCancellationRefundInfoObject = Static<typeof BookingCancellationRefundInfoSchema>;

// --- Response: Pro payment event ---
export const ProPaymentEventResponseSchema = Type.Object({
  pro_payment_event_id: Type.Number(),
  event_type: Type.Enum(ProPaymentEventType),
  detail: Type.Optional(Type.String()),
  amount: Type.Optional(Type.String({ meta: { is_price: true } })),
  created_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'created_at', with_time: true } }),
  ),
});
export type ProPaymentEventResponseObject = Static<typeof ProPaymentEventResponseSchema>;

// --- Response: résumé d'UN paiement d'une réservation (fiche RDV de l'agenda) ---
// Une réservation peut porter plusieurs paiements : le « paiement de réservation » (tunnel client,
// seul porteur de la politique d'annulation) et des paiements ajoutés par le pro (liens, déclarations).
export const AppointmentPaymentSummarySchema = Type.Object({
  pro_payment_id: Type.Integer(),
  status: Type.Enum(ProPaymentStatus),
  payment_type: Type.Enum(ProPaymentType),
  name: Type.Optional(Type.String()),
  amount: Type.String({ meta: { is_price: true, source_key: 'amount_raw' } }),
  amount_raw: Type.Integer(),
  // Net encaissé EN LIGNE et remboursable via Stripe (PAYED / PARTIALLY_REFUNDED avec intent) —
  // alimente les cases « rembourser » de l'annulation. Absent / 0 pour un paiement déclaré à la
  // main (MANUALLY_PAID) ou pas encore encaissé.
  refundable_amount: Type.Optional(Type.String({ meta: { is_price: true, source_key: 'refundable_amount_raw' } })),
  refundable_amount_raw: Type.Optional(Type.Integer()),
  // true pour le paiement créé par le tunnel client (celui qui porte la politique d'annulation).
  is_booking_payment: Type.Boolean(),
  pro_customer_id: Type.Optional(Type.Integer()),
  // « Prénom Nom » du payeur, présent UNIQUEMENT quand il diffère du client de la réservation.
  payer_label: Type.Optional(Type.String()),
  created_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'created_at', with_time: true } }),
  ),
});
export type AppointmentPaymentSummaryObject = Static<typeof AppointmentPaymentSummarySchema>;

// --- Response: Pro payment for pro dashboard ---
export const ProPaymentResponseSchema = Type.Object({
  pro_payment_id: Type.Number(),
  uuid: Type.String(),
  name: Type.Optional(Type.String()),
  status: Type.Enum(ProPaymentStatus),
  payment_type: Type.Enum(ProPaymentType),
  can_capture: Type.Optional(Type.Boolean()),
  can_cancel: Type.Optional(Type.Boolean()),
  can_refund: Type.Optional(Type.Boolean()),
  can_update: Type.Optional(Type.Boolean()),
  // Détachable du rendez-vous : paiement additionnel (lien / déclaration du pro) encore lié à une
  // réservation. Jamais le paiement de réservation (il porte seul la politique d'annulation).
  can_unlink: Type.Optional(Type.Boolean()),
  amount: Type.String({ meta: { is_price: true } }),
  amount_raw: Type.Number(),
  application_fee: Type.String({ meta: { is_price: true } }),
  // Même montant en unité mineure : les surfaces d'affichage masquent la ligne quand les frais
  // sont nuls, ce qui est intestable sur la chaîne formatée (« 0,00 € » est truthy, et son
  // écriture dépend de la locale et de la devise).
  application_fee_raw: Type.Optional(Type.Number()),
  stripe_fee_formatted: Type.Optional(Type.String({ meta: { is_price: true, source_key: 'stripe_fee' } })),
  refunded_amount: Type.Optional(Type.String({ meta: { is_price: true } })),
  refundable_amount: Type.Optional(Type.String({ meta: { is_price: true } })),
  refundable_amount_raw: Type.Optional(Type.Number()),
  amount_excluding_stripe_fee: Type.Optional(Type.String({ meta: { is_price: true } })),
  amount_excluding_stripe_fee_raw: Type.Optional(Type.Number()),
  amount_excluding_application_fee: Type.Optional(Type.String({ meta: { is_price: true } })),
  amount_excluding_application_fee_raw: Type.Optional(Type.Number()),
  booking_cancellation_refund_info: Type.Optional(BookingCancellationRefundInfoSchema),
  // « Reste à encaisser sur place » du booking lié (snapshot pro_payment.remaining_amount_on_site,
  // net des encaissements différés empreinte/CB enregistrée). Absent si paiement hors résa ou reste nul.
  remaining_amount_on_site: Type.Optional(Type.String({ meta: { is_price: true } })),
  description: Type.Optional(Type.String()),
  payment_url: Type.Optional(Type.String()),
  customer_firstname: Type.Optional(Type.String()),
  customer_lastname: Type.Optional(Type.String()),
  customer_email: Type.Optional(Type.String()),
  // Destinataire de l'envoi du lien par SMS (fenêtre de partage).
  customer_phone: Type.Optional(Type.String()),
  pro_customer_id: Type.Optional(Type.Number()),
  reservation_id: Type.Optional(Type.Number()),
  reservation_uuid: Type.Optional(Type.String()),
  reservation_entry_appointment_id: Type.Optional(Type.Number()),
  reservation_start_formatted: Type.Optional(
    Type.String({
      format: 'date-time',
      meta: { source_key: 'reservation_start_pro', with_time: true, is_short: true },
    }),
  ),
  // Carte cadeau (coupon) achetée par ce paiement — alimente le lien « Voir le coupon » du détail.
  voucher_id: Type.Optional(Type.Number()),
  voucher_code: Type.Optional(Type.String()),
  // true si le coupon acheté est encore annulable (statut ACTIVE / PARTIALLY_USED).
  can_cancel_voucher: Type.Optional(Type.Boolean()),
  // Bon UTILISÉ pour payer le booking lié (distinct du coupon acheté ci-dessus) : true s'il reste un usage
  // non restitué → propose « Restituer le bon utilisé » au remboursement.
  can_restore_voucher: Type.Optional(Type.Boolean()),
  restorable_voucher_code: Type.Optional(Type.String()),
  restorable_voucher_amount_raw: Type.Optional(Type.Number()),
  restorable_voucher_amount: Type.Optional(
    Type.String({ meta: { is_price: true, source_key: 'restorable_voucher_amount_raw' } }),
  ),
  // true si le paiement relève d'une COURSE encore active (total connu) : le dialogue de remboursement
  // propose alors « Ce remboursement correspond à un véhicule retiré » (→ `sync_remaining_with_base`).
  can_sync_remaining_with_base: Type.Optional(Type.Boolean()),
  created_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'created_at', with_time: true } }),
  ),
  paid_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'paid_at', with_time: true } }),
  ),
  // Estimation « virement déjà envoyé vers la banque du pro » (date de disponibilité des fonds + calendrier de
  // virement du compte Stripe) ; absent quand on ne peut pas estimer (paiement hors Stripe ou antérieur, virement
  // manuel, virements suspendus, compte Stripe injoignable, paiement remboursé).
  is_paid_out: Type.Optional(Type.Boolean()),
  events: Type.Optional(Type.Array(ProPaymentEventResponseSchema)),
  prestations: Type.Optional(Type.Array(CatalogSelectionResponseSchema)),
  // Une remise s'applique à la réservation liée (total après remise sous le prix normal : promotion du tunnel,
  // promotion ou remise libre posée depuis l'agenda) : badge « Remise » de la ligne de vente. Même règle que la
  // pastille « % » de l'agenda (`has_discount` du rendez-vous). Liste et détail.
  has_discount: Type.Optional(Type.Boolean()),
  // Fiche paiement (détail seulement) : bloc Montant de la réservation liée, celui de l'agenda — prix normal,
  // chaque remise (promotion, code promo, remise libre, montant saisi à la main), total après remise. Absent quand
  // le total enregistré dit déjà qu'il n'y a aucune remise.
  reservation_amounts: Type.Optional(ReservationAmountsSchema),
  // Coupons utilisés sur la réservation liée (tunnel et agenda), valorisés comme sur l'écran de confirmation
  // client (détail seulement) ; absent s'il n'y en a pas.
  reservation_voucher_amount_raw: Type.Optional(Type.Integer()),
  reservation_voucher_amount: Type.Optional(
    Type.String({ meta: { is_price: true, source_key: 'reservation_voucher_amount_raw' } }),
  ),
});
export type ProPaymentResponseObject = Static<typeof ProPaymentResponseSchema>;

// --- Response: List wrapper ---
export const ListProPaymentResponseSchema = Type.Object({
  items: Type.Array(ProPaymentResponseSchema),
  total: Type.Number(),
  has_elements: Type.Boolean(),
});
export type ListProPaymentResponseObject = Static<typeof ListProPaymentResponseSchema>;

// --- Response: ventes d'UN client (onglet « Ventes » de la fiche client) ---
// `Type.Composite` (schéma plat) et non `Type.Intersect` (qui produirait un `allOf` dégradé par
// Swagger/Orval) ni un `Type.Object` re-typé à la main : {items,total,has_elements} reste ainsi
// verrouillé sur `ListProPaymentResponseSchema`, partagé avec la page /sales.
//
// ⚠️ `total` (nombre de lignes listées) ≠ `total_spent_count` : la liste montre AUSSI les
// empreintes bancaires en cours, dont les fonds sont bloqués mais PAS encaissés. Elles n'entrent
// donc ni dans `total_spent` ni dans `total_spent_count`.
export const ListProCustomerSalesResponseSchema = Type.Composite([
  ListProPaymentResponseSchema,
  Type.Object({
    // Total dépensé NET des remboursements sur TOUT l'historique du client, pas la page courante.
    // Chaîne déjà formatée par le service : le wrapper ne passe pas par `transformToSchema`
    // (ses items sont déjà enrichis, un 2e passage écraserait prix et dates).
    total_spent: Type.String({ meta: { is_price: true } }),
    // Nombre de ventes réellement encaissées qui composent `total_spent` — le libellé affiché
    // sous le montant doit compter la même chose que lui.
    total_spent_count: Type.Number(),
  }),
]);
export type ListProCustomerSalesResponseObject = Static<typeof ListProCustomerSalesResponseSchema>;

// --- Response: Payment link ---
export const ProPaymentLinkResponseSchema = Type.Object({
  payment_url: Type.String(),
});
export type ProPaymentLinkResponseObject = Static<typeof ProPaymentLinkResponseSchema>;

// --- Public: End client fetches payment details ---
export const ProPaymentPublicDetailSchema = Type.Object({
  uuid: Type.String(),
  status: Type.Enum(ProPaymentStatus),
  payment_type: Type.Enum(ProPaymentType),
  amount: Type.String({ meta: { is_price: true } }),
  amount_raw: Type.Number(),
  // Nom de la demande saisi par le pro, aussi « Objet » de l'email PAYMENT_REQUEST.
  name: Type.Optional(Type.String()),
  description: Type.Optional(Type.String()),
  professional_name: Type.String(),
  professional_logo: Type.Optional(Type.String()),
  customer_firstname: Type.Optional(Type.String()),
  customer_lastname: Type.Optional(Type.String()),
  customer_email: Type.Optional(Type.String()),
  currency: Type.String(),
  stripe_connect_account_id: Type.String(),
  client_secret: Type.Optional(Type.String()),
  /** Moyens de paiement portés par l'intent Stripe (présent avec client_secret) : les seuls logos à afficher au paiement. */
  payment_method_types: Type.Optional(Type.Array(Type.String())),
  save_card_notice: Type.Optional(Type.Boolean()),
  redirect_url: Type.Optional(Type.String()),
  expiration_date_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'expiration_date', with_time: true } }),
  ),
  prestations: Type.Optional(Type.Array(CatalogSelectionResponseSchema)),
});
export type ProPaymentPublicDetailObject = Static<typeof ProPaymentPublicDetailSchema>;

// --- Public: Polling status ---
export const ProPaymentPublicStatusSchema = Type.Object({
  uuid: Type.String(),
  reservation_uuid: Type.Optional(Type.String()),
  status: Type.Enum(ProPaymentStatus),
  amount: Type.Optional(Type.String({ meta: { is_price: true, source_key: 'amount_raw' } })),
  amount_raw: Type.Optional(Type.Number()),
});
export type ProPaymentPublicStatusObject = Static<typeof ProPaymentPublicStatusSchema>;
